import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TitleCasePipe } from '@angular/common';

import { concatMap, Subject, Subscription } from 'rxjs';
import { ECElementEvent, EChartsOption, Payload, TooltipComponentOption, TreeSeriesOption } from 'echarts';
import { Messages } from 'src/app/constants/messages';
import { ServicecallService } from 'src/app/services/servicecall.service';
import { DataService } from 'src/app/services/data.service';
import { GeneralService } from 'src/app/services/general.service';
import { DeviceStatisticsPayloadModel, HistoryModel, PeripheralChildModel, PeripheralMetricsModel, PeripheralTopicItemModel, ResponseModel, StatusModel, ViewSwitcherOptionModel, ViewType } from 'src/app/model';

@Component({
  selector: 'app-peripherals',
  templateUrl: './peripherals.component.html',
  styleUrls: ['./peripherals.component.css']
})
export class PeripheralsComponent implements OnInit, OnDestroy {

  @Input()  
  set entityData(value: any) {
    this._entityData = value;
    this._peripheralRootNode = value?.uniqueId;
    this._filterPayload = this._setFilterPayload(this._entityData);
    this._getPeripheralMetrics();
  }

  @Output() status = new Subject<StatusModel>();

  // Form
  private _filterPayload: DeviceStatisticsPayloadModel;

  // Peripheral List
  peripheralMetrics: PeripheralMetricsModel;
  viewSwitcherOptions: ViewSwitcherOptionModel[] = [
    {
      tooltip: 'Tile View',
      type: ViewType.TILE_VIEW,
      iconUrl: '../../../../assets/images/icon-tile-view.svg',
      active: true
    },
    {
      tooltip: 'Tree View',
      type: ViewType.TREE_VIEW,
      iconUrl: '../../../../assets/images/icon-tree-view.svg'
    }
  ];
  selViewSwitcherOption: ViewType = ViewType.TILE_VIEW;
  readonly VIEW_TYPE = ViewType;
  readonly MESSAGES = Messages;
  noData: boolean;
  private _entityData :any;
  
  // Chart
  peripheralTreeSeries: TreeSeriesOption = {} as TreeSeriesOption;
  peripheralTreeSeriesUpdate: EChartsOption;
  peripheralTreeTooltipOption: TooltipComponentOption;
  peripheralTreeAutoResize: boolean;
  peripheralDispatchAction: Payload;
  peripheralTreeCursor = {
    dataIndex: 1,
    cursorStyle: 'default'
  };
  private _peripheralRootNode: string;
  private _peripheralTreeRichStyles: object;
  private _peripheralTreeHostRichStyles: object;
  private readonly _PERIPHERAL_BASE_DATA_INDEX = 1;

  // History
  selPeripheral: PeripheralTopicItemModel;
  peripheralHistory: HistoryModel[];
  private _peripheralResponseFlag = new Subject<PeripheralTopicItemModel>();

  // Subscription
  private _peripheralMetricsSubscription: Subscription;
  private _peripheralHistorySubscription: Subscription;
  private _subscription:Subscription;

  constructor(
    private _apiService: ServicecallService,
    private _titlecasePipe: TitleCasePipe,
    private _dataSer: DataService,
    private _generalService: GeneralService
  ) {

    this._setTreeParameters();
    this._getPeripheralHistory();

    this._subscription = this._dataSer.events.subscribe((res: any) => {
      if (res.name === 'apiRefresh') {
        this._setFilterPayload(this._entityData);
        this._setTreeParameters();
        this._getPeripheralMetrics();
      }
    });
  }

  ngOnInit(): void {
    this._setFilterPayload(this._entityData);
   }

  ngOnDestroy(): void {
    this._peripheralMetricsSubscription?.unsubscribe();
    this._peripheralHistorySubscription?.unsubscribe();
    this._subscription?.unsubscribe();
  }

  /**
   * Determines whether tree chart click on
   * @param event
   */
  onTreeChartClick(event: ECElementEvent) {
    if (event?.dataIndex > this._PERIPHERAL_BASE_DATA_INDEX) {
      const selectedPeripheral = this.peripheralMetrics?.series
        ?.find(peripheral => peripheral?.id?.toLowerCase() === (<any>event?.data)?.['type']) ?? {} as PeripheralTopicItemModel;
      this._peripheralResponseFlag.next(selectedPeripheral);
    }
  }

  /**
   * Determines whether block click on
   * @param peripheral
   */
  onBlockClick(peripheral: PeripheralTopicItemModel) {
    this._peripheralResponseFlag.next(peripheral);
  }

  /**
   * Determines whether view switcher select on
   * @param option
   */
  onViewSwitcherSelect(option: ViewSwitcherOptionModel) {
    this.selViewSwitcherOption = option?.type;
  }

  /**
   * Sets tree parameters
   */
  private _setTreeParameters() {
    this.peripheralTreeSeries = {
      data: [],
      layout: 'radial',
      symbol: 'none',
      label: {
        backgroundColor: '#055C7B',
        padding: 10,
        formatter: (params: any) => {
          return [`{${params?.name?.toLowerCase()}|}`, `{root|${params?.name}}`].join('')
        },
        rich: {
          root: {
            padding: [8, 8, 6, 8],
            fontSize: 16,
            verticalAlign: 'middle'
          }
        },
        color: '#FFFFFF',
        borderRadius: 10,
        rotate: 0,
        distance: 0,
        align: 'center',
        verticalAlign: 'middle'
      },
      lineStyle: {
        curveness: 0,
        color: '#DADADA'
      },
      emphasis: {
        focus: 'none',
        disabled: true
      },
      select: {
        disabled: true
      },
      leaves: {  
        label: {
          width: 125,
          backgroundColor: '#F7F9FB',
          shadowColor: '#999999',
          shadowBlur: 7,
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          position: 'right',
          padding: 10,
          formatter: (params: any) => {
            return [
              `{${params?.data?.isActive ? 'titleActive' : 'title'}|${this._titlecasePipe.transform(params?.name)}}`,
              `{${params?.data?.isRecognized}|${params?.data?.isRecognized ? this.MESSAGES?.NOT_RECOGNIZED : ''}}`,
              [
                `{${params?.data?.status}|${params?.value?.toUpperCase()}}`,
                `{${params?.data?.type}|}`
              ].join('')
            ].join('\n');
          },
          rich: {
            title: {
              fontSize: 13,
              padding: [0, 0, 2, 0],
              align: 'left',
              color: '#1C1C1C'
            },
            titleActive: {
              fontSize: 13,
              padding: [0, 0, 2, 0],
              fontWeight: 'bold',
              align: 'left',
              color: '#1C1C1C'
            },
            notRecognized: {
              fontSize: 8,
              align: 'left',
              color: '#767574',
              padding: 1
            },
            recognized: {
              opacity: 0
            },
            statusOn: {
              padding: [6, 8, 3, 8],
              fontSize: 10,
              borderRadius: 10,
              verticalAlign: 'middle',
              align: 'left',
              color: '#008500',
              backgroundColor: '#D7EADB'
            },
            statusOff: {
              padding: [6, 8, 3, 8],
              fontSize: 10,
              borderRadius: 10,
              verticalAlign: 'middle',
              align: 'left',
              color: '#F4493C',
              backgroundColor: '#F4493C1A'
            }
          }
        },
        emphasis: {
          focus: 'none',
          disabled: false,
          label: {
            borderColor: '#055C7B',
            borderWidth: 1
          }
        },
        select: {
          disabled: false,
          label: {
            borderColor: '#055C7B',
            borderWidth: 1
          }
        },
      },
      selectedMode: 'single',
      expandAndCollapse: false,
      animationDuration: 550,
      animationDurationUpdate: 750
    };
    this.peripheralTreeTooltipOption = {
      show: false
    };
    this.peripheralTreeAutoResize = true;
  }

  /**
   * Gets peripheral metrics
   */
  private _getPeripheralMetrics() {
    // Set default
    this.noData = false;
    // API call
    this._peripheralMetricsSubscription = this._apiService.getPeripheralMetrics(this._filterPayload)
    .subscribe({
      next: (result: ResponseModel<PeripheralMetricsModel>) => {
        if (result?.data?.series?.length) {
          this.peripheralMetrics = result?.data;
          // Set tab status
          this.status.next(this.peripheralMetrics?.utilisation);
          // Map content to tree graph
          const treeSeriesChildren: PeripheralChildModel[] = this.peripheralMetrics?.series?.map(metric => {
            this._peripheralTreeRichStyles = {
              ...this._peripheralTreeRichStyles,
              ...{
                [metric?.id?.toLowerCase()]: {
                  backgroundColor: {
                    image: metric?.icon
                  },
                  height: 25,
                  align: 'right',
                  opacity: metric?.isRecognized ? 1 : 0.5
                }
              }
            }
            return {
              id: metric?.id,
              name: metric?.title,
              value: metric?.status?.type,
              status: 'status'.concat(this._titlecasePipe.transform(metric?.status?.type)),
              type: metric?.id?.toLowerCase(),
              isRecognized: metric?.isRecognized ? 'recognized' : 'notRecognized',
              isActive: metric?.isActive
            };
          });
          // Update rich styles based on type from data
          this.peripheralTreeSeries!.leaves!.label!.rich = {
            ...this.peripheralTreeSeries?.leaves?.label?.rich,
            ...this._peripheralTreeRichStyles
          };
          this.peripheralTreeSeries!.label!.rich = {
            ...this.peripheralTreeSeries?.label?.rich,
            ...this._peripheralTreeHostRichStyles
          };
          // Update data TreeSeries
          (<any>this.peripheralTreeSeries.data) = [{
            name: this._peripheralRootNode,
            children: <any>treeSeriesChildren
          }];
          this.peripheralTreeSeries = { ...this.peripheralTreeSeries };
          // Emit data to _peripheralResponseFlag subject, so that _getPeripheralHistory function can start emitting data
          this._peripheralResponseFlag.next(this.peripheralMetrics?.series?.[0]);
        } else {
          this.noData = true;
        }
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.noData = true;
        this._apiService.toasterService({ type: 'Error', msg: errorResponse?.error?.response?.message });
      }
    });
  }

  /**
   * Gets peripheral history
   */
  private _getPeripheralHistory() {
    // API call
    this._peripheralHistorySubscription = this._peripheralResponseFlag
    .pipe(
      concatMap(peripheral => {
        this.selPeripheral = peripheral;
        this._setPeripheralActiveState(peripheral);
        this._setPeripheralTreeActiveState(peripheral);
        return this._apiService.getPeripheralHistory(this._filterPayload, peripheral?.id);
      })
    )
    .subscribe({
      next: (result: ResponseModel<HistoryModel[]>) => {
        this.peripheralHistory = result?.data;
      },
      error: (errorResponse: HttpErrorResponse) => {
        this._apiService.toasterService({ type: 'Error', msg: errorResponse?.error?.response?.message });
      }
    });
  }

  /**
   * Sets peripheral active state
   * @param selectedPeripheral
   */
  private _setPeripheralActiveState(selectedPeripheral: PeripheralTopicItemModel) {
    this.peripheralMetrics.series = this.peripheralMetrics?.series?.map(peripheral => {
      return { ...peripheral, isActive: peripheral?.id === selectedPeripheral?.id ? true : false };
    });
  }

  /**
   * Sets peripheral tree active state
   * @param selectedPeripheral 
   */
  private _setPeripheralTreeActiveState(selectedPeripheral: PeripheralTopicItemModel) {
    this.peripheralTreeSeries.data[0].children = this.peripheralTreeSeries?.data?.[0]?.children?.map((peripheralTree, index) => {
      if (peripheralTree?.id === selectedPeripheral?.id) {
        this.peripheralDispatchAction = {
          type: 'select',
          seriesIndex: 0,
          dataIndex: index + 2
        };
      }
      return { ...peripheralTree, isActive: peripheralTree?.id === selectedPeripheral?.id ? true : false };
    });
    this.peripheralTreeSeriesUpdate = {
      series: [
        {
          data: this.peripheralTreeSeries?.data
        }
      ]
    };
  }

  /**
   * Sets filter payload
   * @param value 
   * @returns  
   */
  private _setFilterPayload(value: any) {
    const [startTime, endTime] = this._generalService?.getStartEndTimeByDate();
    return {      
      deviceId: value?.device?.deviceId,
      timeRange: {
        from: startTime,
        to: Date.now()
      }
    };
  }
}
